// Copyright (c) Ping. All rights reserved.
//
// BACKGROUND post-signup recovery backup. Replaces the old blocking
// `creatingRecoveryBackup` gate: a fresh signup now lands straight in
// the app, and this fire-and-forget task secures the account behind a
// small, non-blocking status indicator.
//
// Steps (all off the critical render path):
//   1. Generate a fresh 12-word BIP39 mnemonic via the SDK.
//   2. Build the V3 recovery blob (SDK Argon2id backup + HKDF-wrapped
//      auth companion) and POST it to /v1/recovery/blobs.
//   3. Mark the local recovery-backup status `completed` (same marker
//      `App.tsx` / Settings already read).
//   4. Persist the mnemonic LOCALLY, encrypted at rest, so it can be
//      revealed later (Settings → Account recovery) and PURGED once the
//      user confirms they've written it down.
//
// The mnemonic plaintext NEVER leaves the device.

import { MessagingClient } from 'ping-openmls-sdk';

import { pingToast,postRecoveryBlob } from '@ping/ui';

import { log } from '@core/log/log';
import { db } from '@core/persistence/db';

import {
  readRecoveryBackupStatus,
  RECOVERY_BACKUP_KV_KEY,
  type RecoveryBackupStatus,
} from './RecoveryBackupSetupHost';
import { buildRecoveryBlobV3 } from './recoveryBackupV3';
import {
  retrieveRecoveryMnemonic,
  storeRecoveryMnemonic,
} from './recoveryMnemonicStore';

// Module-level guard so a StrictMode double-invoke or a re-fired effect
// can't kick two concurrent backups against the same account.
let inFlight = false;

async function markBackupStatus(status: RecoveryBackupStatus): Promise<void> {
  await db.open();
  await db.settings_kv.put({
    key: RECOVERY_BACKUP_KV_KEY,
    value: status,
    updatedAt: Date.now(),
  });
}

/**
 * Run the background recovery backup for a fresh signup. Fire-and-forget:
 * the caller does NOT await this and the AppShell is never blocked on it.
 * Best-effort — any failure is logged + surfaced as a non-fatal toast,
 * leaving the user free to set recovery up later from Settings.
 */
export async function runPostSignupRecoveryBackup(): Promise<void> {
  if (inFlight) return;
  inFlight = true;

  // Idempotency: if this install ALREADY has a completed backup AND the
  // mnemonic is still locally retrievable, the recovery phrase is revealable —
  // do nothing (a re-verify that kept the phrase must not needlessly rotate the
  // key). We only (re)generate when there is no revealable phrase: a fresh
  // signup, or a sign-in after a sign-out wiped the local mnemonic. Runs before
  // the toast so a no-op stays invisible.
  try {
    const [status, existingMnemonic] = await Promise.all([
      readRecoveryBackupStatus(),
      retrieveRecoveryMnemonic(),
    ]);
    if (status?.kind === 'completed' && existingMnemonic) {
      inFlight = false;
      return;
    }
  } catch {
    /* best-effort — fall through and (re)build the backup */
  }

  // Transient "working" indicator. Held visible for the duration of the
  // backup; replaced by the "ready" toast on success.
  pingToast.info({
    title: 'Securing your account…',
    description: 'Setting up your recovery backup.',
    visibilityMs: 60_000,
  });

  try {
    const mnemonic = await MessagingClient.generateMnemonicPhrase();
    // Stash the mnemonic locally (encrypted at rest) BEFORE the upload, so
    // the reveal screen + the recovery tour can always surface the exact
    // words the blob is encrypted under — even if the build/upload below
    // throws. Stashing only after a successful upload meant any upload
    // hiccup left the user unable to reveal the phrase (and therefore
    // unable to restore: `decrypt_backup` rejects a phrase that never
    // matched the stored blob). Purged on confirm.
    await storeRecoveryMnemonic(mnemonic);

    const ciphertext = await buildRecoveryBlobV3(mnemonic);
    await postRecoveryBlob({ ciphertext });
    await markBackupStatus({
      kind: 'completed',
      uploadedAt: Date.now(),
      bytes: ciphertext.length,
    });

    log.info({
      event: 'recovery.signup.backgroundUploaded',
      bytes: ciphertext.length,
    });

    pingToast.hide();
    pingToast.success({
      title: 'Recovery backup ready',
      description: 'Save your recovery phrase from Settings.',
    });
  } catch (err) {
    log.warn({
      event: 'recovery.signup.backgroundFailed',
      message: (err as Error).message,
    });
    pingToast.hide();
    pingToast.error({
      title: "Couldn't finish your recovery backup",
      description: 'You can set it up anytime in Settings → Account recovery.',
    });
  } finally {
    inFlight = false;
  }
}
